import * as ImagePicker from "expo-image-picker";
import { useQuery } from "@tanstack/react-query";

import { isApiError, unwrap } from "@brad-tipper/app-frontend";

import { api, sessionStore } from "~/api/client";
import { API_URL } from "~/lib/constants";

export type MediaBucket = "plant-photos" | "collection-maps";

export type PickedImage = {
  uri: string;
  width: number;
  height: number;
  mimeType: string;
};

export async function pickImage(
  options: { allowsEditing?: boolean } = {},
): Promise<PickedImage | null> {
  const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
  if (!permission.granted) {
    throw new Error("Florafolio needs photo library access to add images.");
  }

  const result = await ImagePicker.launchImageLibraryAsync({
    mediaTypes: ["images"],
    allowsEditing: options.allowsEditing ?? false,
    quality: 0.85,
  });
  const asset = result.canceled ? null : result.assets[0];
  if (!asset) return null;

  return {
    uri: asset.uri,
    width: asset.width,
    height: asset.height,
    mimeType: asset.mimeType ?? "image/jpeg",
  };
}

export async function captureImage(): Promise<PickedImage | null> {
  const permission = await ImagePicker.requestCameraPermissionsAsync();
  if (!permission.granted) {
    throw new Error("Florafolio needs camera access to photograph plants.");
  }

  const result = await ImagePicker.launchCameraAsync({ quality: 0.85 });
  const asset = result.canceled ? null : result.assets[0];
  if (!asset) return null;

  return {
    uri: asset.uri,
    width: asset.width,
    height: asset.height,
    mimeType: asset.mimeType ?? "image/jpeg",
  };
}

/**
 * Uploads the image to the API's private file store and returns its `path`
 * (`<user id>/<file>`; the API derives ownership from the first segment). The
 * upload is a raw body, so it is not part of the generated client. Plant photos
 * and uploaded site plans share one store; `bucket` only names the use.
 */
export async function uploadImage(
  _bucket: MediaBucket,
  _userId: string,
  image: PickedImage,
) {
  const body = await fetch(image.uri).then((response) =>
    response.arrayBuffer(),
  );
  const token = await sessionStore.getAccessToken();
  const response = await fetch(`${API_URL}/v1/uploads`, {
    method: "POST",
    headers: {
      "Content-Type":
        image.mimeType === "image/jpg" ? "image/jpeg" : image.mimeType,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
  });
  const payload = (await response.json().catch(() => null)) as {
    path?: string;
    detail?: string;
  } | null;
  if (!response.ok || !payload?.path) {
    throw new Error(
      payload?.detail ?? `Unable to upload that image (${response.status}).`,
    );
  }
  return payload.path;
}

export async function removeImage(_bucket: MediaBucket, path: string) {
  try {
    await unwrap(api.deleteUpload({ query: { path } }));
  } catch (error) {
    // Already gone is fine.
    if (!(isApiError(error) && error.status === 404)) throw error;
  }
}

/** Files are private, so display needs a short-lived signed URL. */
export function useSignedUrl(
  bucket: MediaBucket,
  path: string | null | undefined,
) {
  return useQuery({
    queryKey: ["florafolio-signed-url", bucket, path ?? ""],
    enabled: Boolean(path),
    staleTime: 45 * 60 * 1000,
    queryFn: async () =>
      (await unwrap(api.getUploadUrl({ query: { path: path as string } }))).url,
  });
}
